
import React from 'react';
import { ProcurementItem } from '@/types/database.types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ProcurementItemsListProps {
  items: ProcurementItem[];
  showTitle?: boolean;
}

export function ProcurementItemsList({ items, showTitle = true }: ProcurementItemsListProps) {
  if (!items || items.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">No items found for this procurement.</p>
        </CardContent>
      </Card>
    );
  }

  const totalAmount = items.reduce((sum, item) => sum + (item.price_per_unit * item.quantity), 0);
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="space-y-4">
      {showTitle && (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Products ({items.length})</h3>
          <div className="text-sm text-muted-foreground">
            Total: {totalQuantity.toFixed(2)} kg | Br {totalAmount.toFixed(2)}
          </div>
        </div>
      )}
      
      <div className="grid gap-4">
        {items.map((item, index) => (
          <Card key={item.id}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <CardTitle className="text-base">{item.product_name}</CardTitle>
                {item.quality_grade && (
                  <Badge variant="outline">{item.quality_grade}</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Quantity:</span>
                  <div className="font-medium">{item.quantity} kg</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Price/Unit:</span>
                  <div className="font-medium">Br {item.price_per_unit.toFixed(2)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Total:</span>
                  <div className="font-medium">Br {(item.price_per_unit * item.quantity).toFixed(2)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Item:</span>
                  <div className="font-medium">#{index + 1}</div>
                </div>
              </div>
              
              {item.notes && (
                <div className="mt-3 pt-3 border-t">
                  <span className="text-muted-foreground text-sm">Notes:</span>
                  <p className="text-sm mt-1">{item.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center text-lg font-semibold">
            <span>Total Summary:</span>
            <span>{totalQuantity.toFixed(2)} kg | Br {totalAmount.toFixed(2)}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
